import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import SnackbarCustom from '../../components/SnackbarCustom';
import ApiService from '../../services/ApiService';
import { SkeletonText } from '../../components/Skeleton/SkeletonText';
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { formatCurrency } from '../../utils/formatCurrency';
import { Close, Save } from '@mui/icons-material';
import DialogCustom from '../../components/DialogCustom';
import { LoadingButton } from '@mui/lab';
import SimpleMDE from 'react-simplemde-editor';

const CreateLKPDecision = () => {
  // Hooks
  const navigate = useNavigate();
  const { lkpEncode } = useParams();

  // States
  const [loading, setLoading] = useState({
    fetch: false,
    submit: false,
  });
  const [snackbar, setSnackbar] = useState({
    isOpen: false,
    type: '', // "error" | "info" | "success" | "warning"
    message: '',
  });
  const [confimationOpen, setConfimationOpen] = useState(false);
  const [dataLkp, setDataLkp] = useState({});
  const [errorLkp, setErrorLkp] = useState({ isOpen: false, message: '' });

  // Return Functions

  // Void Functions
  const fetchLkp = async () => {
    setLoading({ ...loading, fetch: true });
    try {
      const resLkp = await ApiService.getLkpDecision(lkpEncode);
      if (!resLkp.data.isOk) throw resLkp.data.message;
      const data = resLkp.data.payload;
      if (data.lkpDecisionDate !== '') {
        data.perluInvestigasi = data.needInvestigation;
        data.perluIncidentAlert = data.needIncidentAlert;
        data.idPelaksanaInvestigasi = data.listPelaksanaInvestigasi?.find(
          (item) => item.name === data.pelaksanaInvestigasi
        )?.id;
        setDataLkp(data);
      }
      setDataLkp(data);
      setLoading({ ...loading, fetch: false });
    } catch (error) {
      setErrorLkp({ isOpen: true, message: error });
    }
  };
  const onSubmit = async () => {
    try {
      const resUpdate = await ApiService.postLkpDecision({
        idLkp: dataLkp.idLkp,
        needIncidentAlert: dataLkp.perluIncidentAlert,
        needInvestigation: dataLkp.perluInvestigasi,
        needUpdateLkp: dataLkp.isNeedUpdateLkp,
        idPelaksanaInvestigasi:
          dataLkp.perluInvestigasi && dataLkp.idPelaksanaInvestigasi
            ? dataLkp.idPelaksanaInvestigasi
            : null,
        flagIsHipo: dataLkp.flagIsHipo,
        flagIsReportable: dataLkp.flagIsReportable,
        flagIsWorkRelated:
          dataLkp.flagIsReportable && dataLkp.flagIsWorkRelated,
        flagIsRecordable:
          dataLkp.flagIsReportable &&
          dataLkp.flagIsWorkRelated &&
          dataLkp.flagIsRecordable,
        updateReason: dataLkp.updateReason,
      });
      if (!resUpdate.data.isOk) {
        setSnackbar({
          isOpen: true,
          type: 'error',
          message: resUpdate.data.message,
        });
        setConfimationOpen(false);
      } else {
        navigate('/hsse/incident/dashboardlkp');
      }
    } catch (error) {
      setSnackbar({
        isOpen: true,
        type: 'error',
        message: 'Error posting lkp decision',
      });
    }
  };

  // UseEffects
  useEffect(() => {
    if (lkpEncode) fetchLkp();
  }, []);

  const leftColumnLabels = [
    'No. LKP',
    'Judul',
    'Region',
    'Lokasi',
    'Decision Oleh',
    'Tanggal dan Jam Decision',
  ];
  const rightColumnLabels = [
    'Dibuat Oleh',
    'Tanggal dan Jam Kejadian',
    'Kategori HIPO',
    'Severity',
    'Probability',
    'Reportable',
  ];

  const getDataValue = (label) => {
    switch (label) {
      case 'No. LKP':
        return dataLkp.noLkp;
      case 'Judul':
        return dataLkp.judulLaporan;
      case 'Region':
        return dataLkp.regionalName;
      case 'Lokasi':
        return dataLkp.lokasiName;
      case 'Decision Oleh':
        return dataLkp.lkpDecisionBy || '-';
      case 'Tanggal dan Jam Decision':
        return dataLkp.lkpDecisionDate || '-';
      case 'Dibuat Oleh':
        return dataLkp.pembuatDokumen;
      case 'Tanggal dan Jam Kejadian':
        return dataLkp.tglKejadian;
      case 'Kategori HIPO':
        return dataLkp.isHipo;
      case 'Severity':
        return dataLkp.potensiTingkatKeparahan;
      case 'Probability':
        return dataLkp.probabilitasKejadian;
      case 'Reportable':
        return dataLkp.isReportAble;
      default:
        return '';
    }
  };

  const labelStyle = {
    ...styles.label, // existing label styles
    flex: 1,
    textAlign: 'left',
    paddingRight: '10px',
    marginTop: '10px',
  };

  const valueStyle = {
    ...styles.value, // existing value styles
    flex: 2,
    paddingLeft: '10px',
    marginTop: '10px',
  };

  const renderColumn = (labels) => (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      {labels.map((label, index) => (
        <div
          key={index}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            marginBottom: index === labels.length - 1 ? '10px' : 0,
          }}
        >
          <p style={{ ...labelStyle, marginTop: index === 0 ? '10px' : 0 }}>
            {label}
          </p>
          <SkeletonText
            loading={loading.fetch}
            customValue=":"
            text={getDataValue(label)}
            style={{ ...valueStyle, marginTop: index === 0 ? '10px' : 0 }}
          />
        </div>
      ))}
    </div>
  );

  return (
    <>
      <h2 style={{ marginLeft: '25px' }}>LKP DECISION</h2>
      <div
        style={{
          margin: '0 25px',
          backgroundColor: '#fff',
          borderRadius: 5,
          paddingBottom: 25,
        }}
      >
        {/* DATA LKP */}
        <div style={{ display: 'flex' }}>
          {renderColumn(leftColumnLabels)}
          {renderColumn(rightColumnLabels)}
        </div>

        <div style={{ margin: '0 25px' }}>
          {/* DESKRIPSI / KATEGORI */}
          <p>Deskripsi Singkat Kejadian:</p>
          <SimpleMDE
            value={dataLkp.deskripsiSingkat}
            // options={{
            //   spellChecker: false,
            //   hideIcons: [
            //     "link",
            //     "image",
            //     "preview",
            //     "fullscreen",
            //     "guide",
            //     "side-by-side",
            //   ],
            // }}
          />
          {/* <SkeletonText
            loading={loading.fetch}
            text={dataLkp.deskripsiSingkat}
            style={{
              padding: "10px",
              backgroundColor: "#f5f4ff",
              borderRadius: 5,
            }}
          /> */}
          <p>Kategori Insiden:</p>
          <SkeletonText
            loading={loading.fetch}
            text={dataLkp.kategoriInsident}
            style={{
              padding: '10px',
              backgroundColor: '#f5f4ff',
              borderRadius: 5,
            }}
          />

          {/* ESTIMATE IMPACT */}
          <p>Estimate Impact:</p>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell>Nama</TableCell>
                  <TableCell>Content</TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell align="right">Jml/Nilai Kerugian</TableCell>
                  <TableCell>Satuan</TableCell>
                  <TableCell align="right">Nilai/Satuan Volume</TableCell>
                  <TableCell align="right">Hasil Konversi</TableCell>
                  <TableCell>Keterangan</TableCell>
                </TableRow>
              </TableHead>
              {dataLkp.listDetailEstimateContent ? (
                <TableBody>
                  {dataLkp.listDetailEstimateContent.map((dt, idx) => (
                    <TableRow
                      key={idx}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell>
                        <SkeletonText loading={loading.fetch} text={dt.nama} />
                      </TableCell>
                      <TableCell>
                        <SkeletonText
                          loading={loading.fetch}
                          text={dt.contentName}
                        />
                      </TableCell>
                      <TableCell align={dt.itemName ? 'left' : 'center'}>
                        <SkeletonText
                          loading={loading.fetch}
                          text={dt.itemName || '-'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <SkeletonText
                          loading={loading.fetch}
                          text={formatCurrency(
                            parseFloat(dt.jumlahOrNilaiKerugian).toFixed(2)
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <SkeletonText
                          loading={loading.fetch}
                          text={dt.satuan}
                        />
                      </TableCell>
                      <TableCell
                        align={dt.nilaiSatuanVolume ? 'right' : 'center'}
                      >
                        <SkeletonText
                          loading={loading.fetch}
                          text={
                            parseFloat(dt.nilaiSatuanVolume).toFixed(2) || '-'
                          }
                        />
                      </TableCell>
                      <TableCell align={dt.hasilKonversi ? 'right' : 'center'}>
                        <SkeletonText
                          loading={loading.fetch}
                          text={
                            formatCurrency(
                              parseFloat(dt.hasilKonversi).toFixed(2)
                            ) || '-'
                          }
                        />
                      </TableCell>
                      <TableCell align={dt.keterangan ? 'left' : 'center'}>
                        <SkeletonText
                          loading={loading.fetch}
                          text={dt.keterangan || '-'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              ) : (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No Data
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>

          {/* PERLU EDIT */}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={6}>
              <p>Perlu Edit:</p>
              <FormControl
                sx={{ width: 200, backgroundColor: '#f5f4ff' }}
                size="small"
              >
                <Select
                  value={dataLkp.isNeedUpdateLkp || false}
                  onChange={(e) =>
                    setDataLkp({ ...dataLkp, isNeedUpdateLkp: e.target.value })
                  }
                  displayEmpty
                  inputProps={{ 'aria-label': 'Perlu Edit' }}
                  sx={{ textAlign: 'center' }}
                  disabled={dataLkp.lkpDecisionDate !== ''}
                >
                  <MenuItem value={true}>Ya</MenuItem>
                  <MenuItem value={false}>Tidak</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {dataLkp.isNeedUpdateLkp && (
              <Grid item xs={6}>
                <p>Kategori HIPO</p>
                <FormControl size="small" component="fieldset">
                  <RadioGroup
                    row
                    aria-label="kategori-hipo"
                    name="kategori-hipo"
                    value={dataLkp.flagIsHipo}
                    onChange={(e) =>
                      setDataLkp({
                        ...dataLkp,
                        flagIsHipo: e.target.value === 'true',
                      })
                    }
                  >
                    <FormControlLabel
                      value={true}
                      control={<Radio />}
                      label="HIPO"
                      disabled={dataLkp.lkpDecisionDate !== ''}
                    />
                    <FormControlLabel
                      value={false}
                      control={<Radio />}
                      label="Non-HIPO"
                      disabled={dataLkp.lkpDecisionDate !== ''}
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            )}
          </Grid>

          {dataLkp.isNeedUpdateLkp && (
            <Grid
              container
              spacing={2}
              alignItems="center"
              sx={{ mt: 2, mb: 2 }}
            >
              <Grid item>
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    aria-label="isReportable"
                    name="isReportable"
                    value={dataLkp.flagIsReportable}
                    onChange={(e) =>
                      setDataLkp({
                        ...dataLkp,
                        flagIsReportable: e.target.value === 'true',
                      })
                    }
                  >
                    <FormControlLabel
                      value={true}
                      control={<Radio />}
                      label="Reportable"
                      disabled={dataLkp.lkpDecisionDate !== ''}
                    />
                    <FormControlLabel
                      value={false}
                      control={<Radio />}
                      label="Not Reportable"
                      disabled={dataLkp.lkpDecisionDate !== ''}
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {dataLkp.flagIsReportable && (
                <Grid item>
                  <FormControl component="fieldset">
                    <RadioGroup
                      row
                      aria-label="isWorkRelated"
                      name="isWorkRelated"
                      value={dataLkp.flagIsWorkRelated}
                      onChange={(e) =>
                        setDataLkp({
                          ...dataLkp,
                          flagIsWorkRelated: e.target.value === 'true',
                        })
                      }
                    >
                      <FormControlLabel
                        value={true}
                        control={<Radio />}
                        label="Work Related"
                        disabled={dataLkp.lkpDecisionDate !== ''}
                      />
                      <FormControlLabel
                        value={false}
                        control={<Radio />}
                        label="Not Work Related"
                        disabled={dataLkp.lkpDecisionDate !== ''}
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              )}

              {dataLkp.flagIsReportable && dataLkp.flagIsWorkRelated && (
                <Grid item>
                  <FormControl component="fieldset">
                    <RadioGroup
                      row
                      aria-label="isRecordable"
                      name="isRecordable"
                      value={dataLkp.flagIsRecordable}
                      onChange={(e) =>
                        setDataLkp({
                          ...dataLkp,
                          flagIsRecordable: e.target.value === 'true',
                        })
                      }
                    >
                      <FormControlLabel
                        value={true}
                        control={<Radio />}
                        label="Recordable"
                        disabled={dataLkp.lkpDecisionDate !== ''}
                      />
                      <FormControlLabel
                        value={false}
                        control={<Radio />}
                        label="Not Recordable"
                        disabled={dataLkp.lkpDecisionDate !== ''}
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          )}

          {dataLkp.isNeedUpdateLkp && (
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12}>
                <TextField
                  label="Alasan Edit LKP"
                  multiline
                  rows={4}
                  variant="outlined"
                  value={dataLkp.updateReason}
                  onChange={(e) =>
                    setDataLkp({ ...dataLkp, updateReason: e.target.value })
                  }
                  fullWidth
                  disabled={dataLkp.lkpDecisionDate !== ''}
                />
              </Grid>
            </Grid>
          )}

          {/* INVESTIGASI */}
          <div style={{ display: 'flex' }}>
            <div style={{ flex: 1 }}>
              <p>Perlu Investigasi:</p>
              <FormControl
                sx={{ width: 200, backgroundColor: '#f5f4ff' }}
                size="small"
              >
                <Select
                  value={dataLkp.perluInvestigasi || false}
                  onChange={(e) => {
                    setDataLkp({
                      ...dataLkp,
                      perluInvestigasi: e.target.value,
                      idPelaksanaInvestigasi:
                        e.target.value === false
                          ? ''
                          : dataLkp.idPelaksanaInvestigasi,
                    });
                  }}
                  displayEmpty
                  inputProps={{ 'aria-label': 'Perlu Investigasi' }}
                  sx={{ textAlign: 'center' }}
                  disabled={dataLkp.lkpDecisionDate !== ''}
                >
                  <MenuItem value={true}>Ya</MenuItem>
                  <MenuItem value={false}>Tidak</MenuItem>
                </Select>
              </FormControl>
              <p>Pelaksana Investigasi:</p>
              <FormControl
                disabled={!dataLkp.perluInvestigasi}
                sx={{ width: 200, backgroundColor: '#f5f4ff' }}
                size="small"
              >
                <Select
                  value={dataLkp.idPelaksanaInvestigasi || ''}
                  onChange={(e) => {
                    setDataLkp({
                      ...dataLkp,
                      idPelaksanaInvestigasi: e.target.value,
                    });
                  }}
                  displayEmpty
                  inputProps={{ 'aria-label': 'Pelaksana Investigasi' }}
                  sx={{ textAlign: 'center' }}
                  disabled={dataLkp.lkpDecisionDate !== ''}
                  defaultValue={dataLkp.idPelaksanaInvestigasi || ''}
                >
                  {!dataLkp.perluInvestigasi ? (
                    <MenuItem value={''} disabled>
                      -
                    </MenuItem>
                  ) : null}
                  {dataLkp.listPelaksanaInvestigasi?.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div style={{ flex: 1 }}>
              <p>Perlu Incident Alert:</p>
              <FormControl
                sx={{ width: 200, backgroundColor: '#f5f4ff' }}
                size="small"
              >
                <Select
                  value={dataLkp.perluIncidentAlert || false}
                  onChange={(e) => {
                    setDataLkp({
                      ...dataLkp,
                      perluIncidentAlert: e.target.value,
                    });
                  }}
                  displayEmpty
                  inputProps={{ 'aria-label': 'Perlu Incident Alert' }}
                  sx={{ textAlign: 'center' }}
                  disabled={dataLkp.lkpDecisionDate !== ''}
                >
                  <MenuItem value={true}>Ya</MenuItem>
                  <MenuItem value={false}>Tidak</MenuItem>
                </Select>
              </FormControl>
            </div>
          </div>
        </div>
        {dataLkp.lkpDecisionDate === '' && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={() => setConfimationOpen(true)}
            >
              Submit
            </Button>
          </div>
        )}
      </div>

      {/* SNACKBAR */}
      <SnackbarCustom
        duration={3000}
        type={snackbar.type || 'warning'}
        open={snackbar.isOpen}
        onClose={() => setSnackbar({ ...snackbar, isOpen: false })}
        message={snackbar.message}
      />

      {/* Dialog Konfirmasi Save */}
      <DialogCustom
        open={confimationOpen}
        handleClose={() => setConfimationOpen(false)}
        title={
          <>
            Notifikasi
            <IconButton
              aria-label="close"
              onClick={() => setConfimationOpen(false)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <Close />
            </IconButton>
          </>
        }
        content={
          <>
            Apakah anda yakin akan submit dokument ini? <br />
            Pastikan data sudah terisi dengan lengkap dan benar
          </>
        }
        actions={
          <>
            <Button
              onClick={() => setConfimationOpen(false)}
              variant="outlined"
              disabled={loading.submit}
            >
              Batal
            </Button>
            <LoadingButton
              onClick={onSubmit}
              autoFocus
              variant="contained"
              loading={loading.submit}
              startIcon={<Save />}
              loadingPosition="start"
            >
              {!loading.submit ? 'Ya,' : ''} Submit
            </LoadingButton>
          </>
        }
      />
      <DialogCustom
        open={errorLkp.isOpen}
        handleClose={() => setErrorLkp((prev) => ({ ...prev, isOpen: false }))}
        title={<>Terjadi Kesalahan</>}
        content={<p style={{ minWidth: 200 }}>{errorLkp.message}</p>}
        actions={
          <>
            <Button
              onClick={() => navigate('/hsse/incident/dashboardlkp')}
              autoFocus
              variant="contained"
            >
              Kembali
            </Button>
          </>
        }
      />
    </>
  );
};

const styles = {
  label: { margin: '5px 0px 5px 20px', fontWeight: 'bold' },
  value: {
    margin: '5px 20px',
  },
};

export default CreateLKPDecision;
